import { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Clock, Loader2, FileText, Coffee, CalendarPlus } from 'lucide-react';
import Header from '../../components/Header';
import { API } from './_helpers';
import WeekGrid from './WeekGrid';
import LeavePanel from './LeavePanel';
import TimesheetsPanel from './TimesheetsPanel';
import CalendarSubscribeModal from './CalendarSubscribeModal';
import PublicScheduleConfigModal from './PublicScheduleConfigModal';

export default function RosterPage() {
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState('roster');
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showPublicCfg, setShowPublicCfg] = useState(false);

  useEffect(() => {
    axios.get(`${API}/roster/access`, { withCredentials: true })
      .then(r => setAccess(r.data))
      .catch(() => setAccess({ is_manager: false, user_id: null }));
  }, []);

  if (!access) {
    return (
      <div className="min-h-screen bg-background" data-testid="roster-page">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="roster-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#0d2644' }}>
              {access.is_manager ? 'Rostering' : 'My roster'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {access.is_manager
                ? 'Plan shifts, publish to staff, and review leave + timesheets.'
                : 'View your upcoming shifts, request leave, and submit timesheets.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {access.is_manager && (
              <button onClick={() => setShowPublicCfg(true)} data-testid="roster-public-cfg-btn"
                className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-muted/40"
                title="Get a public read-only roster URL to share">
                <FileText className="w-3.5 h-3.5" /> Public schedule
              </button>
            )}
            <button onClick={() => setShowSubscribe(true)} data-testid="roster-subscribe-btn"
              className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-muted/40"
              title="Subscribe to your shifts in Apple/Google/Outlook calendar">
              <CalendarPlus className="w-3.5 h-3.5" /> Send to my calendar
            </button>
            <div className="flex items-center gap-1 rounded-lg border bg-card p-1" role="tablist">
              {[
                { id: 'roster',    label: 'Roster',     icon: Calendar },
                { id: 'leave',     label: 'Leave',      icon: Coffee },
                { id: 'timesheets',label: 'Timesheets', icon: Clock },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`roster-tab-${t.id}`}
                  className={`text-sm font-semibold px-3.5 py-1.5 rounded-md inline-flex items-center gap-1.5 ${tab===t.id ? 'bg-[#0d2644] text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {tab === 'roster'     && <WeekGrid isManager={access.is_manager} meId={access.user_id} />}
        {tab === 'leave'      && <LeavePanel isManager={access.is_manager} meId={access.user_id} />}
        {tab === 'timesheets' && <TimesheetsPanel isManager={access.is_manager} meId={access.user_id} />}
      </div>
      {showSubscribe && <CalendarSubscribeModal onClose={() => setShowSubscribe(false)} />}
      {showPublicCfg && <PublicScheduleConfigModal onClose={() => setShowPublicCfg(false)} />}
    </div>
  );
}
